/**
 * GTD Query Service Factory
 * 
 * Creates query service instances following the Published Language pattern.
 * This factory provides the stable interface that downstream contexts depend on.
 * 
 * Eric Evans approved: Factory function within inbox-management BC that creates
 * query services exposing Published Language contracts.
 */

import { MongoClient } from "@swoft/persistence";
import { InboxItemAggregateRepository } from '../repositories/InboxItemAggregateRepository';
import { InboxQueryCriteria, InboxItemSummary, ProjectQueryCriteriaContract, ProjectSummary } from '../../../../integration/contracts';
import { ProjectRepository } from '../../../project-management/infrastructure/repositories/ProjectRepository';

/**
 * GTD Query Service Interface - Published Language
 * 
 * This interface defines the stable query contracts that downstream contexts
 * can depend on. Changes to this interface constitute breaking changes.
 */
export interface GTDQueryService {
  listInboxItems(criteria: InboxQueryCriteria): Promise<InboxItemSummary[]>;
  getInboxItem(id: string): Promise<InboxItemSummary | null>;
  
  // Project queries - Published Language
  listProjects(criteria: ProjectQueryCriteriaContract): Promise<ProjectSummary[]>;
  getProject(id: string): Promise<ProjectSummary | null>;
  
  // Next actions interface (for existing adapter)
  listNextActions(criteria: any): Promise<any[]>;
}

/**
 * Person lookup service interface for enriching GTD data
 */
export interface PersonLookupService {
  getPersonById(id: string): Promise<{
    id: string;
    displayName: string;
    email: string;
    roleType: 'Human' | 'AiAgent';
  } | null>;
}

/**
 * Implementation of GTD Query Service using domain repositories
 */
class GTDQueryServiceImpl implements GTDQueryService {
  private inboxRepository: InboxItemAggregateRepository;
  private projectRepository: ProjectRepository;
  private personLookupService?: PersonLookupService;

  constructor(_mongoClient: MongoClient | undefined, personLookupService?: PersonLookupService) {
    this.inboxRepository = new InboxItemAggregateRepository();
    this.projectRepository = new ProjectRepository();
    this.personLookupService = personLookupService;
  }

  /**
   * Extract a user-friendly title from content
   */
  private extractTitle(content: string): string {
    if (!content) return 'Untitled';
    
    // Remove markdown headers and extract first meaningful line
    const firstLine = content.split('\n')[0].replace(/^#+\s*/, '').trim();
    
    // If first line is too long, truncate it
    if (firstLine.length > 60) {
      return firstLine.substring(0, 57) + '...';
    }
    
    return firstLine || 'Untitled';
  }

  /**
   * List inbox items using Published Language contracts
   */
  async listInboxItems(criteria: InboxQueryCriteria): Promise<InboxItemSummary[]> {
    const items = await this.inboxRepository.findByFilters({
      status: criteria.status,
      limit: criteria.limit,
      offset: criteria.offset
    });

    // Enrich with person data if service is available
    const enrichedItems = await Promise.all(
      items.map(async (item) => {
        let capturedByPerson = null;
        
        if (this.personLookupService && item.capturedByPersonId) {
          try {
            capturedByPerson = await this.personLookupService.getPersonById(item.capturedByPersonId);
          } catch (error) {
            // Gracefully handle person lookup failures
            console.warn(`Failed to lookup person ${item.capturedByPersonId}:`, error);
          }
        }

        return {
          id: item.id,
          content: item.originalContent,
          text: item.originalContent,  // Backward compatibility
          title: this.extractTitle(item.originalContent),
          status: item.processingStatus as 'unprocessed' | 'processed',
          capturedAt: item.capturedAt.toISOString(),
          createdAt: item.capturedAt,  // Backward compatibility
          capturedBy: item.capturedByPersonId,
          capturedByPerson,
          clarification: item.clarification,
          description: item.clarification,  // Backward compatibility
          isActionable: item.isActionable,
          tags: [] // TODO: Add tags support to domain model
        };
      })
    );

    return enrichedItems;
  }

  /**
   * Get single inbox item using Published Language contracts
   */
  async getInboxItem(id: string): Promise<InboxItemSummary | null> {
    const item = await this.inboxRepository.findById(id);
    
    if (!item) return null;

    // Enrich with person data if service is available
    let capturedByPerson = null;
    
    if (this.personLookupService && item.capturedByPersonId) {
      try {
        capturedByPerson = await this.personLookupService.getPersonById(item.capturedByPersonId);
      } catch (error) {
        console.warn(`Failed to lookup person ${item.capturedByPersonId}:`, error);
      }
    }

    return {
      id: item.id,
      content: item.originalContent,
      text: item.originalContent,  // Backward compatibility
      title: this.extractTitle(item.originalContent),
      status: item.processingStatus as 'unprocessed' | 'processed',
      capturedAt: item.capturedAt.toISOString(),
      createdAt: item.capturedAt,  // Backward compatibility
      capturedBy: item.capturedByPersonId,
      capturedByPerson,
      clarification: item.clarification,
      description: item.clarification,  // Backward compatibility
      isActionable: item.isActionable,
      tags: [] // TODO: Add tags support to domain model
    };
  }

  /**
   * List projects using Published Language contracts
   */
  async listProjects(criteria: ProjectQueryCriteriaContract): Promise<ProjectSummary[]> {
    try {
      let projects;
      
      if (criteria.status) {
        projects = await this.projectRepository.findByStatus(criteria.status);
      } else if (criteria.area) {
        projects = await this.projectRepository.findByArea(criteria.area);
      } else {
        projects = await this.projectRepository.findAll();
      }

      // Map domain objects to Published Language contracts with error handling
      return projects
        .filter(project => project && project.name) // Filter out invalid projects
        .map(project => ({
          id: project.id,
          title: project.name,
          vision: project.desiredOutcome,
          status: project.status as 'active' | 'completed' | 'someday-maybe' | 'cancelled',
          area: project.area,
          reviewDate: project.reviewDate?.toISOString(),
          createdAt: project.createdAt.toISOString(),
          nextActionCount: project.nextActionIds.length
        }));
    } catch (error) {
      // Gracefully handle database/domain errors by returning empty array
      console.warn('GTD Projects query failed, returning empty array:', error);
      return [];
    }
  }

  /**
   * Get single project using Published Language contracts
   */
  async getProject(id: string): Promise<ProjectSummary | null> {
    const project = await this.projectRepository.findById(id);
    
    if (!project) return null;

    return {
      id: project.id,
      title: project.name,
      vision: project.desiredOutcome,
      status: project.status as 'active' | 'completed' | 'someday-maybe' | 'cancelled',
      area: project.area,
      reviewDate: project.reviewDate?.toISOString(),
      createdAt: project.createdAt.toISOString(),
      nextActionCount: project.nextActionIds.length
    };
  }

  /**
   * Next actions query - to be implemented when project-management BC is ready
   */
  async listNextActions(_criteria: any): Promise<any[]> {
    // TODO: Implement proper next actions query when project-management BC is ready
    return [];
  }
}

/**
 * Factory function for creating GTD Query Service instances
 * 
 * This is the stable interface that downstream contexts depend on.
 * The implementation can change, but this factory signature should remain stable.
 */
export function createGTDQueryService(
  mongoClient?: MongoClient, 
  personLookupService?: PersonLookupService
): GTDQueryService {
  // Parameters are kept for backward compatibility, but we use the shared connection
  return new GTDQueryServiceImpl(mongoClient, personLookupService);
}